import * as React from "react";
import { Box, Button, MenuItem, Select } from "@mui/material";
import { AddRounded, FileDownloadOutlined } from "@mui/icons-material";
import { AdmLayout } from "../../../layouts/base/adm.layout";
import { UserModel } from "./table/user.model";
import { SimpleSnackbar } from "../../../components/snackbar";
import { MessageDialog } from "../../../components/message-dialog";
import { AdminFilterBar, AdminPageHeader, countLabel, DataTable, EmptyState } from "../../../components/ui";
import { useUsersPage } from "./use-users-page";
import { buildUserColumns } from "./users-columns";
import InviteUserModal from "./components/InviteUserModal";

export const UsersPage: React.FC = () => {
    const {
        loading,
        openDialog,
        setOpenDialog,
        showSnackbar,
        setShowSnackbar,
        snackbarMessage,
        userId,
        users,
        query,
        typeFilter,
        planFilter,
        inviteOpen,
        setInviteOpen,
        inviteForm,
        setInviteField,
        inviteSaving,
        plans,
        isInviteValid,
        openInvite,
        submitInvite,
        cancelPlan,
        exporting,
        handleExportNotRenewed,
        handlePageChange,
        handlePageSizeChange,
        handleCancelPlan,
        onQueryChange,
        onTypeChange,
        onPlanChange,
    } = useUsersPage();

    const columns = buildUserColumns({ onCancelPlan: handleCancelPlan });

    return (
        <AdmLayout loading={loading} hasLogo={true}>
            <MessageDialog
                open={openDialog}
                message={`Deseja cancelar o plano de ${users.data.find((v) => v.id === userId)?.name}?`}
                onConfirm={() => cancelPlan()}
                confirmButtonText="Sim"
                onClose={() => setOpenDialog(false)}
                cancelButtonText="Cancelar"
            />

            <SimpleSnackbar show={showSnackbar} message={snackbarMessage} onDismiss={() => setShowSnackbar(false)} />

            <AdminPageHeader
                title="Usuários"
                subtitle="Gestão de usuários da plataforma"
                action={
                    <Box sx={{ display: "flex", gap: 1.5, flexWrap: "wrap" }}>
                        <Button
                            variant="outlined"
                            startIcon={<FileDownloadOutlined />}
                            onClick={handleExportNotRenewed}
                            disabled={exporting}
                            sx={{ height: 44, borderRadius: "12px", fontWeight: 600 }}
                        >
                            {exporting ? "Exportando..." : "Exportar não renovados"}
                        </Button>
                        <Button
                            variant="contained"
                            startIcon={<AddRounded />}
                            onClick={openInvite}
                            sx={{ height: 44, borderRadius: "12px", fontWeight: 600 }}
                        >
                            Convidar usuário
                        </Button>
                    </Box>
                }
            />

            <AdminFilterBar
                search={query}
                onSearch={onQueryChange}
                placeholder="Buscar usuário..."
                filters={
                    <>
                        <Select size="small" value={typeFilter} onChange={onTypeChange} sx={{ height: 40, minWidth: 130, borderRadius: "10px" }}>
                            <MenuItem value="name">Nome</MenuItem>
                            <MenuItem value="email">Email</MenuItem>
                        </Select>

                        <Select size="small" value={planFilter} onChange={onPlanChange} sx={{ height: 40, minWidth: 170, borderRadius: "10px" }}>
                            <MenuItem value="all">Todos</MenuItem>
                            <MenuItem value="yes">Com plano</MenuItem>
                            <MenuItem value="no">Sem plano</MenuItem>
                        </Select>
                    </>
                }
                count={countLabel(users.pagination.total, users.pagination.total)}
            />

            <DataTable<UserModel>
                columns={columns}
                rows={users.data}
                rowKey={(row) => String(row.id)}
                loading={loading}
                page={users.pagination.currentPage}
                pageSize={users.pagination.perPage}
                totalRows={users.pagination.total}
                onPageChange={handlePageChange}
                onPageSizeChange={handlePageSizeChange}
                minWidth={700}
                countLabel={countLabel(users.pagination.total, users.pagination.total)}
                emptyState={<EmptyState title="Nenhum usuário encontrado" description="Tente ajustar os filtros de busca." />}
            />

            <InviteUserModal
                open={inviteOpen}
                onClose={() => setInviteOpen(false)}
                form={inviteForm}
                setField={setInviteField}
                plans={plans}
                isValid={isInviteValid}
                saving={inviteSaving}
                onSubmit={submitInvite}
            />
        </AdmLayout>
    );
};
